import { MonthlyOrderStore } from '../stores/monthlyOrder';
import { MonthlyOrdersStore } from '../stores/monthlyOrders';
import { apiMessagesSaveMessagesV2 } from './apiMessages';
import { apiManager } from '../REST';
import {
    IApproveMonthlyOrder,
    IApproveMonthlyOrderAPI,
    IBillingSystemCompany,
    IGetMonthlyOrderAvailableOrdersAPI,
    IMonthlyOrder,
    IMonthlyOrderAPI,
    IMonthlyOrderOrder,
    IMonthlyOrderOrderAPI,
    IMonthlyOrderPostLinkUnlinkAPI,
    IMonthlyOrderPostPoNumber,
    ISelectList,
    IUsageProductsItem,
    IUsageProductsItemAPI,
} from '../interfaces';
import { getCurrencyShortCodeById, mapApi2UiField, mapUi2ApiField } from '../instruments';

export const setMonthlyOrder = (order: IMonthlyOrder): void => {
    const StoreInstance = MonthlyOrderStore.getInstance();
    StoreInstance.setOrder(order);
};

const monthlyOrderAPI2UI = (monthlyOrder: IMonthlyOrderAPI): IMonthlyOrder => {
    const item: IMonthlyOrder = mapApi2UiField(monthlyOrder) as IMonthlyOrder;
    item.companyId = mapApi2UiField(item?.companyId || {}) as IBillingSystemCompany;
    item.linkedOrders = monthlyOrder.linked_orders?.map((i) => mapApi2UiField(i) as IMonthlyOrderOrder) || [];
    item.invoice = monthlyOrder.invoice ? mapApi2UiField(monthlyOrder.invoice) : undefined;
    item.usageProducts = monthlyOrder?.usage_products?.map((i: IUsageProductsItemAPI) => {
        const product: IUsageProductsItem = mapApi2UiField(i);
        product.currency = {
            id: i.currency_id,
            abbreviation: i.currency_id ? getCurrencyShortCodeById(i.currency_id.toString()) : undefined,
        };

        return product;
    });
    return item;
};

export const getMonthlyOrderById = async (id: string): Promise<void> => {
    const { resp, isError } = await apiManager.getApi('GET_MONTHLY_ORDER', { id: id });

    if (!isError && resp) {
        setMonthlyOrder(monthlyOrderAPI2UI(resp as IMonthlyOrderAPI));
    }
};

const setMonthlyOrderAvailableOrdersSelect = (list: Array<IMonthlyOrderOrderAPI>): void => {
    const selectList: ISelectList = list.map((i) => ({ id: i.id.toString(), name: `#${i.id} - ${i.description}` }));

    const StoreInstance = MonthlyOrderStore.getInstance();
    StoreInstance.setOrderAvailableOrdersSelect(selectList);
};

export const getMonthlyOrderAvailableOrdersById = async (id: string): Promise<void> => {
    const { resp, isError } = await apiManager.getApi('GET_MONTHLY_ORDER_AVAILABLE_ORDERS', { id: id });

    if (!isError && resp) {
        setMonthlyOrderAvailableOrdersSelect((resp as IGetMonthlyOrderAvailableOrdersAPI).available_orders || []);
    } else {
        setMonthlyOrderAvailableOrdersSelect([]);
    }
};

export const postMonthlyOrderCancelById = async (id: string): Promise<void> => {
    const { resp, isError } = await apiManager.getApi('POST_MONTHLY_ORDER_CANCEL', { id: id });

    if (!isError && resp) {
        setMonthlyOrder(monthlyOrderAPI2UI(resp as IMonthlyOrderAPI));
    }
};

export const postMonthlyOrderApproveById = async (id: string, order: IApproveMonthlyOrder): Promise<boolean> => {
    const body: IApproveMonthlyOrderAPI = mapUi2ApiField(order);
    const { resp, isError } = await apiManager.getApi('POST_MONTHLY_ORDER_APPROVE', { id: id }, body);
    if (resp) {
        if (!isError) {
            setMonthlyOrder(monthlyOrderAPI2UI(resp as IMonthlyOrderAPI));
        } else {
            apiMessagesSaveMessagesV2(resp.errors);
        }
    }
    return !isError;
};

export const postMonthlyMarkAsPaidById = async (id: string): Promise<void> => {
    const { resp, isError } = await apiManager.getApi('POST_MONTHLY_ORDER_MARK_AS_PAID', { id: id });
    if (resp) {
        if (!isError) {
            setMonthlyOrder(monthlyOrderAPI2UI(resp as IMonthlyOrderAPI));
        }
    }
};

type IPostMonthlyOrderLinkUnlinkOrder = {
    monthlyOrder: string;
    orderId: string;
    linkening: boolean;
};

export const postMonthlyOrderLinkUnlinkOrder = async ({
    monthlyOrder,
    orderId,
    linkening,
}: IPostMonthlyOrderLinkUnlinkOrder): Promise<boolean> => {
    const body: IMonthlyOrderPostLinkUnlinkAPI = { monthly_order: { order_id: Number(orderId) } };
    const { resp, isError } = await apiManager.getApi(
        linkening ? 'POST_MONTHLY_ORDER_LINK_ORDER' : 'POST_MONTHLY_ORDER_UNLINK_ORDER',
        { id: monthlyOrder },
        body,
    );
    if (resp) {
        if (!isError) {
            setMonthlyOrder(monthlyOrderAPI2UI(resp as IMonthlyOrderAPI));
        } else {
            apiMessagesSaveMessagesV2(resp.errors);
        }
    }
    return !isError;
};

type IPostMonthlyOrderPoNumber = {
    id: number;
    numberOrder: string;
};

export const patchMonthlyOrderPoNumber = async ({ id, numberOrder }: IPostMonthlyOrderPoNumber): Promise<boolean> => {
    const body: IMonthlyOrderPostPoNumber = { monthly_order: { purchase_order_number: numberOrder } };
    const { resp, isError } = await apiManager.getApi('PATCH_MONTHLY_ORDER_PO_NUMBER', { id: id.toString() }, body);
    if (resp) {
        if (!isError) {
            setMonthlyOrder(monthlyOrderAPI2UI(resp as IMonthlyOrderAPI));
        } else {
            apiMessagesSaveMessagesV2(resp.errors);
        }
    }
    return !isError;
};

export function cleanMonthlyOrderStore(): void {
    const UserInstance = MonthlyOrdersStore.getInstance();
    UserInstance.cleanStore();
}
